// ============================================================================
// Adaptateurs DTO gestion -> libs de paie + calculs du hub (L09)
// ============================================================================
// GLUE, pas une lib metier (« aucune nouvelle lib » du MASTER vise les libs de
// paie/profil, deja portees). Les libs pures (lib/salary/*) attendent la forme
// PoC (snake_case : echelon_max, pct_from_prev, hourly_rate, effective_date) ; le
// DTO gestion arrive en camelCase. On adapte ICI, une fois, plutot que d'inliner
// la meme conversion dans ClassGrid + SalaryProgression + ManagementTab.

import type { GridClass, ManagementCompensationView, RateChange } from '../../hooks/useManagement';
import type { SalaryClass, SalaryClassEchelon } from '../../lib/types/firestore';
import { buildSalaryTimeline, type SalaryTimeline } from '../../lib/salary/progression';
import { maxEchelonForNiveau } from '../../lib/salary/niveau';

/** Forme minimale attendue par niveau.ts / off-grid.ts (echelons + niveaux). */
export type LibClass = Pick<SalaryClass, 'echelons' | 'niveaux'>;

/** GridClass (DTO camelCase) -> forme PoC consommee par les libs de paie. */
export function gridClassToLib(cls: GridClass): LibClass {
  return {
    echelons: cls.echelons.map((e) => ({
      echelon: e.echelon,
      rate: e.rate,
      rate_2025: null,
      pct_from_prev: e.pctFromPrev,
    })) as SalaryClassEchelon[],
    niveaux: cls.niveaux.map((n) => ({
      level: n.level,
      label: n.label,
      echelon_max: n.echelonMax,
      description: null,
    })),
  };
}

/** Echelons au format SalaryClassEchelon (buildSalaryTimeline). */
export function gridEchelons(cls: GridClass): SalaryClassEchelon[] {
  return cls.echelons.map((e) => ({
    echelon: e.echelon,
    rate: e.rate,
    rate_2025: null,
    pct_from_prev: e.pctFromPrev,
  })) as SalaryClassEchelon[];
}

/** rate_history DTO -> forme buildSalaryTimeline (hourly_rate/effective_date). */
export function toRealRates(rates: RateChange[]): { hourly_rate: number; effective_date: string }[] {
  return rates.map((r) => ({ hourly_rate: r.hourlyRate, effective_date: r.effectiveDate }));
}

/**
 * Construit la frise salariale du hub a partir du DTO gestion complet (M-763).
 * Retourne null si la fiche n'a pas de classe resolue dans la grille (la frise
 * n'a alors aucun ancrage — l'appelant rend une carte sans graphique).
 */
export function buildHubTimeline(
  comp: ManagementCompensationView,
  currentYear: number,
): { timeline: SalaryTimeline; cls: GridClass } | null {
  const record = comp.record;
  if (!record || !record.classId) return null;
  const cls = comp.classes.find((c) => c.id === record.classId);
  if (!cls) return null;

  const niveauCap = maxEchelonForNiveau(gridClassToLib(cls), record.currentNiveau);
  const pastPoints = comp.salaryHistory
    .filter((h) => h.year < currentYear && h.year >= currentYear - 5)
    .map((h) => ({ year: h.year, annual: h.annual, roleLabel: h.role ?? '' }));

  const timeline = buildSalaryTimeline({
    maxEchelonCap: niveauCap,
    currentEchelon: record.currentEchelon,
    hireDate: record.hireDate,
    echelons: gridEchelons(cls),
    augGrillePct: cls.augGrillePct,
    hours: {
      base_hours_per_week: comp.work?.weeklyHours ?? cls.baseHoursPerWeek,
      weeks_per_year: cls.weeksPerYear,
    },
    pastPoints,
    realRates: toRealRates(comp.rateHistory),
    departureDate: record.departureDate,
    currentYear,
    gridYear: comp.gridYear,
  });
  return { timeline, cls };
}
